import {ExternalForecastYear} from './external-forecast.model';

const FALLBACK_LOCALE = "en-US";

/** App locales such as "en-US-cxone" / "en-US-Dummy" are not BCP-47; reduce them to a locale Intl accepts. */
export function toIntlLocale(appLocale: string | null | undefined): string {
  if (!appLocale) {
    return FALLBACK_LOCALE;
  }
  const candidate = appLocale.replace(/-(cxone|dummy)$/i, "");
  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
}

/**
 * AC-13: the year containing today's local date if present; otherwise the first year when the plan
 * is entirely in the future, or the last year when it is entirely in the past.
 */
export function pickDefaultYear(years: ExternalForecastYear[], today: Date): ExternalForecastYear {
  const current = today.getFullYear();
  const match = years.find(y => y.year === current);
  if (match) {
    return match;
  }
  return current < years[0].year ? years[0] : years[years.length - 1];
}

/** Mean of the values, or null when there are none (rows without data show a blank Average). */
export function averageOf(values: number[]): number | null {
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
}

export function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {maximumFractionDigits: 0}).format(value);
}

export function formatPercent(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1}).format(value / 100);
}

/**
 * The fiscal year that starts in `startMonth` (1-12) of `year`: the twelve months from "year-startMonth" on,
 * drawn from whichever snapshot years hold them. Starting in January is the calendar year itself, so the
 * server total is kept; any other start spans two calendar years, so its total is summed from the months.
 */
export function buildFiscalYear(years: ExternalForecastYear[], year: number, startMonth: number): ExternalForecastYear {
  const calendar = years.find(y => y.year === year);
  if (startMonth === 1) {
    return calendar;
  }
  const key = (y: number) => `${y}-${String(startMonth).padStart(2, "0")}`;
  const months = years.flatMap(y => y.months).filter(m => m.month >= key(year) && m.month < key(year + 1));
  return {year, months, total: months.reduce((sum, m) => sum + m.contactsReceived, 0)};
}
